import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';

// PHA-503/PHA-527: per-user "last clinic the user explicitly selected", stashed
// so we can restore it after a server-session reset (Auth0 re-auth, BFF cookie
// loss) instead of silently defaulting to selectableOrgs[0]. AuthContext used
// to write/read this key directly; ownership now lives here so there is one
// source of persistence.
export const LAST_ORG_KEY_PREFIX = 'phanova_last_org_id:';

// PHA-1123: this browser TAB's own remembered clinic. `req.session.currentOrganizationId`
// on the BFF is one slot per LOGIN, shared by every tab on that cookie -- picking a clinic
// in one tab silently overwrites what every other open tab gets back from
// `GET /session/organization/current`. sessionStorage is the fix: unlike localStorage and
// cookies, the browser gives every tab its own isolated copy that survives reloads/navigation
// within that tab but is never shared with (or inherited by) a sibling tab. Checked BEFORE the
// server's `is_selected` value in `initialize()` below, so a tab that already made its own
// choice keeps reasserting it on every reinit instead of adopting whatever another tab set.
export const TAB_ORG_KEY_PREFIX = 'phanova_tab_org_id:';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5802';

export interface OrganizationOption {
  id: string;
  name: string;
}

interface CurrentOrgResponse {
  organization_id?: string | null;
  organization_name?: string | null;
  default_program?: string | null;
  is_selected?: boolean;
}

interface OrganizationContextType {
  // Empty string if no selection has been made (multi-clinic non-internal user
  // with no remembered choice). Otherwise a specific org id, or "ALL" for
  // internal users in the all-organizations view.
  orgId: string;
  orgName: string;
  defaultProgram: string | null;
  // Full list of orgs the user can pick from. For internal users this includes
  // an "ALL" entry; "ALL" is filtered out for non-internal users even if the
  // backend returns it.
  orgList: OrganizationOption[];
  isInternal: boolean;
  loading: boolean;
  setOrg: (orgId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | null>(null);

export const useOrganization = () => {
  const ctx = useContext(OrganizationContext);
  if (!ctx) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return ctx;
};

// PHA-1166: for providers that may also be rendered without an OrganizationProvider
// (unit tests mount PatientTabsProvider on its own).
export const useOptionalOrganization = (): OrganizationContextType | null =>
  useContext(OrganizationContext);

const fetchJson = async (path: string, init?: RequestInit) => {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    credentials: 'include',
    ...init,
  });
  if (!response.ok) {
    throw new Error(`Request failed: ${path} ${response.status}`);
  }
  return response.json();
};

const postCurrentOrg = async (orgId: string): Promise<CurrentOrgResponse | null> => {
  try {
    const response = await fetch(`${apiBaseUrl}/session/organization/current`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: orgId }),
    });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
};

const safeGetItem = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};

const safeSetItem = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch {}
};

// PHA-1123: sessionStorage, not localStorage -- per-tab isolation is the entire point here.
const safeGetSessionItem = (key: string): string | null => {
  try { return sessionStorage.getItem(key); } catch { return null; }
};

const safeSetSessionItem = (key: string, value: string) => {
  try { sessionStorage.setItem(key, value); } catch {}
};

export const OrganizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.id ?? null;
  const isInternal = Boolean(user?.is_internal);

  const [orgId, setOrgId] = useState<string>('');
  const [orgName, setOrgName] = useState<string>('');
  const [defaultProgram, setDefaultProgram] = useState<string | null>(null);
  const [orgList, setOrgList] = useState<OrganizationOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Guard against stale initialize() runs setting state for a previous user.
  const initRunIdRef = useRef(0);

  const applyCurrent = useCallback((current: CurrentOrgResponse | null | undefined) => {
    if (current?.organization_id) {
      setOrgId(String(current.organization_id));
      setOrgName(current.organization_name ?? '');
      setDefaultProgram(current.default_program ?? null);
    } else {
      setOrgId('');
      setOrgName('');
      setDefaultProgram(null);
    }
  }, []);

  const initialize = useCallback(async () => {
    const runId = ++initRunIdRef.current;
    if (!userId) {
      setOrgList([]);
      applyCurrent(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const lastOrgKey = `${LAST_ORG_KEY_PREFIX}${userId}`;
    const tabOrgKey = `${TAB_ORG_KEY_PREFIX}${userId}`;

    try {
      const [listResult, currentResult] = await Promise.allSettled([
        fetchJson('/session/organization/list', { method: 'GET' }),
        fetchJson('/session/organization/current', { method: 'GET' }),
      ]);

      if (initRunIdRef.current !== runId) return;

      const rawList = listResult.status === 'fulfilled' && Array.isArray(listResult.value?.organizations)
        ? listResult.value.organizations
        : [];
      const parsedList: OrganizationOption[] = rawList
        .filter((o: any) => o?.id && o?.name)
        .map((o: any) => ({ id: String(o.id), name: String(o.name) }))
        .filter((o: OrganizationOption) => isInternal || o.id !== 'ALL');
      setOrgList(parsedList);

      const accessibleIds = new Set(parsedList.map((o) => o.id));
      const current = currentResult.status === 'fulfilled' ? (currentResult.value as CurrentOrgResponse) : null;

      // PHA-1123: THIS TAB's own choice, if it has ever made one, wins over the shared
      // server session -- otherwise a reload/reinit in this tab silently adopts whatever
      // clinic a DIFFERENT open tab most recently picked (they share one cookie, so the
      // server session is common to both). Re-asserting it via postCurrentOrg also repairs
      // the shared session back to this tab's value, so a sibling tab's own reinit isn't
      // left pointed at a stale choice either.
      const tabStored = safeGetSessionItem(tabOrgKey);
      if (tabStored && (accessibleIds.has(tabStored) || (tabStored === 'ALL' && isInternal))) {
        const next = await postCurrentOrg(tabStored);
        if (initRunIdRef.current !== runId) return;
        if (next) {
          applyCurrent(next);
          safeSetItem(lastOrgKey, tabStored);
          return;
        }
      }

      if (current?.is_selected && current.organization_id) {
        // Server already has a confirmed selection — capture it so we can
        // restore the same value after a future session reset.
        const id = String(current.organization_id);
        applyCurrent(current);
        if (accessibleIds.has(id) || id === 'ALL') {
          safeSetItem(lastOrgKey, id);
          // PHA-1123: this is the FIRST time this tab has resolved a clinic — stamp it
          // into tab storage so the tab keeps reasserting ITS OWN value on future
          // reinits, rather than re-adopting whatever a sibling tab sets afterward.
          safeSetSessionItem(tabOrgKey, id);
        }
        return;
      }

      // No server-side selection. Restore from per-user localStorage if it is
      // still valid for the user's accessible list.
      const stored = safeGetItem(lastOrgKey);
      if (stored && (accessibleIds.has(stored) || (stored === 'ALL' && isInternal))) {
        const next = await postCurrentOrg(stored);
        if (initRunIdRef.current !== runId) return;
        if (next) {
          applyCurrent(next);
          safeSetSessionItem(tabOrgKey, stored);
          return;
        }
      }

      if (isInternal) {
        // Internal users with no remembered selection default to "All
        // Organizations" — matches prior behavior for fresh internal logins.
        const next = await postCurrentOrg('ALL');
        if (initRunIdRef.current !== runId) return;
        if (next) {
          applyCurrent(next);
          safeSetItem(lastOrgKey, 'ALL');
          safeSetSessionItem(tabOrgKey, 'ALL');
          return;
        }
      }

      const selectableOrgs = parsedList.filter((o) => o.id !== 'ALL');
      if (selectableOrgs.length === 1) {
        // Single-clinic user — no real choice to make; restore it.
        const target = selectableOrgs[0];
        const next = await postCurrentOrg(target.id);
        if (initRunIdRef.current !== runId) return;
        if (next) {
          applyCurrent(next);
          safeSetItem(lastOrgKey, target.id);
          safeSetSessionItem(tabOrgKey, target.id);
          return;
        }
      }

      // PHA-503: multi-clinic non-internal user with no remembered selection.
      // Do NOT silently pick selectableOrgs[0] — that was the path by which
      // session resets quietly re-stamped users to "Aussie" (alphabetically
      // first) and produced the dropdown/list desync. Leave it unset so the
      // dropdown surfaces "Select organization".
      // PHA-585: pass null, not `current` — `current` may still carry the
      // backend's auto-selected org which would silently mis-assign the user.
      applyCurrent(null);
    } catch (error) {
      console.error('Failed to initialize organization:', error);
    } finally {
      if (initRunIdRef.current === runId) setLoading(false);
    }
  }, [userId, isInternal, applyCurrent]);

  useEffect(() => {
    // Wait for auth to settle before fetching — the session cookie is what the
    // backend uses to authorize /session/organization/* endpoints, and we want
    // a single deterministic load per user.
    if (authLoading) return;
    initialize();
  }, [authLoading, initialize]);

  const setOrg = useCallback(async (newOrgId: string) => {
    if (!userId) throw new Error('No authenticated user');
    const next = await postCurrentOrg(newOrgId);
    if (!next) {
      throw new Error('Failed to set organization');
    }
    applyCurrent(next);
    safeSetItem(`${LAST_ORG_KEY_PREFIX}${userId}`, newOrgId);
    // PHA-1123: an explicit in-tab choice is this tab's strongest signal of intent —
    // record it so a later reinit in THIS tab (reload, auth refresh) reasserts it
    // instead of picking up whatever a sibling tab set in the shared server session.
    safeSetSessionItem(`${TAB_ORG_KEY_PREFIX}${userId}`, newOrgId);
  }, [userId, applyCurrent]);

  const refresh = useCallback(async () => {
    await initialize();
  }, [initialize]);

  const value = useMemo<OrganizationContextType>(() => ({
    orgId,
    orgName,
    defaultProgram,
    orgList,
    isInternal,
    loading,
    setOrg,
    refresh,
  }), [orgId, orgName, defaultProgram, orgList, isInternal, loading, setOrg, refresh]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationProvider;
